import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsGeoAlt, BsGlobe } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useVisibleFieldDefs } from '../../../models/customFields/queries';
import CustomFieldsSection from '../../common/CustomFieldsSection/CustomFieldsSection';

const SectionLabel = ({ icon: Icon, children }) => (
	<div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-bgBase-800 mb-2">
		<Icon className="w-3 h-3" /> {children}
	</div>
);

SectionLabel.propTypes = {
	icon: PropTypes.elementType.isRequired,
	children: PropTypes.node.isRequired,
};

const FieldLabel = ({ label, required }) => (
	<label className="block text-[11px] font-medium text-bgBase-800 mb-1">
		{label}
		{required && <span className="text-warning-500 ml-0.5">*</span>}
	</label>
);

FieldLabel.propTypes = {
	label: PropTypes.string.isRequired,
	required: PropTypes.bool,
};

const NewCompanyModal = ({ isOpen, onClose, onCreated }) => {
	const [fields, setFields] = useState({
		companyName: '',
		emailAddress: '',
		phoneNumber: '',
		websiteUrl: '',
		addressStreet: '',
		addressPostal: '',
		addressCity: '',
		linkedinUrl: '',
		instagramUrl: '',
		facebookUrl: '',
	});
	const [customFields, setCustomFields] = useState({});
	const [submitting, setSubmitting] = useState(false);

	const { data: companyFieldDefs = [] } = useVisibleFieldDefs('company');

	// Determine which default fields are required (per org config)
	const requiredKeys = useMemo(() => {
		const set = new Set();
		for (const def of companyFieldDefs) {
			if (def.isDefault && def.isRequired) set.add(def.fieldKey);
		}
		return set;
	}, [companyFieldDefs]);

	// Custom field defs that are NOT default (rendered via CustomFieldsSection)
	const hasCustomFields = companyFieldDefs.some((f) => !f.isDefault);

	const hasRequiredFieldsMissing = useMemo(() => {
		// Check default fields
		for (const key of requiredKeys) {
			const val = fields[key];
			if (!val || (typeof val === 'string' && !val.trim())) return true;
		}
		// Check custom fields
		return companyFieldDefs.some((def) => {
			if (def.isDefault || !def.isRequired) return false;
			const val = customFields[def.id];
			return val === undefined || val === null || val === '';
		});
	}, [requiredKeys, fields, companyFieldDefs, customFields]);

	const set = (key) => (e) => setFields((prev) => ({ ...prev, [key]: e.target.value }));

	// Check which default fields are visible (org may have hidden some)
	const visible = useMemo(() => {
		const set = new Set();
		for (const def of companyFieldDefs) {
			if (def.isDefault) set.add(def.fieldKey);
		}
		return set;
	}, [companyFieldDefs]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!fields.companyName.trim() || hasRequiredFieldsMissing) return;
		setSubmitting(true);
		try {
			const payload = {};

			// Add all default field values that are not empty
			for (const [key, val] of Object.entries(fields)) {
				if (val !== '' && val !== null && val !== undefined) {
					payload[key] = typeof val === 'string' ? val.trim() : val;
				}
			}

			// Add custom field values
			const cf = Object.fromEntries(Object.entries(customFields).filter(([, v]) => v !== '' && v !== undefined));
			if (Object.keys(cf).length > 0) payload.customFields = cf;

			await onCreated(payload);
			onClose();
		} finally {
			setSubmitting(false);
		}
	};

	const isReq = (key) => requiredKeys.has(key);

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={handleSubmit} size="l">
			<Panel.Header onClose={onClose} subtitle="Erstellen Sie ein neues Unternehmen in Ihrem CRM">
				Neues Unternehmen
			</Panel.Header>
			<Panel.Body>
				<div className="mb-5">
					<FieldLabel label="Firmenname" required />
					<TextInput
						value={fields.companyName}
						onChange={set('companyName')}
						placeholder="z. B. Mustermann GmbH"
						required
						autoFocus
					/>
				</div>

				{(visible.has('emailAddress') || visible.has('phoneNumber')) && (
					<div className="mb-5">
						<SectionLabel icon={BsBuilding}>Kontaktdaten</SectionLabel>
						<div className="grid grid-cols-2 gap-3">
							{visible.has('emailAddress') && (
								<div>
									<FieldLabel label="E-Mail" required={isReq('emailAddress')} />
									<TextInput
										type="email"
										value={fields.emailAddress}
										onChange={set('emailAddress')}
										placeholder="info@firma.de"
									/>
								</div>
							)}
							{visible.has('phoneNumber') && (
								<div>
									<FieldLabel label="Telefon" required={isReq('phoneNumber')} />
									<TextInput
										value={fields.phoneNumber}
										onChange={set('phoneNumber')}
										placeholder="+49 ..."
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{(visible.has('addressStreet') || visible.has('addressPostal') || visible.has('addressCity')) && (
					<div className="mb-5">
						<SectionLabel icon={BsGeoAlt}>Adresse</SectionLabel>
						<div className="space-y-3">
							{visible.has('addressStreet') && (
								<div>
									<FieldLabel label="Straße" required={isReq('addressStreet')} />
									<TextInput
										value={fields.addressStreet}
										onChange={set('addressStreet')}
										placeholder="Musterstraße 1"
									/>
								</div>
							)}
							<div className="grid grid-cols-3 gap-3">
								{visible.has('addressPostal') && (
									<div>
										<FieldLabel label="PLZ" required={isReq('addressPostal')} />
										<TextInput
											value={fields.addressPostal}
											onChange={set('addressPostal')}
											placeholder="12345"
										/>
									</div>
								)}
								{visible.has('addressCity') && (
									<div className="col-span-2">
										<FieldLabel label="Stadt" required={isReq('addressCity')} />
										<TextInput
											value={fields.addressCity}
											onChange={set('addressCity')}
											placeholder="Berlin"
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{(visible.has('websiteUrl') ||
					visible.has('linkedinUrl') ||
					visible.has('instagramUrl') ||
					visible.has('facebookUrl')) && (
					<div className="mb-5">
						<SectionLabel icon={BsGlobe}>Web & Social</SectionLabel>
						<div className="space-y-3">
							{visible.has('websiteUrl') && (
								<div>
									<FieldLabel label="Website" required={isReq('websiteUrl')} />
									<TextInput
										value={fields.websiteUrl}
										onChange={set('websiteUrl')}
										placeholder="https://www.firma.de"
									/>
								</div>
							)}
							<div className="grid grid-cols-3 gap-3">
								{visible.has('linkedinUrl') && (
									<div>
										<FieldLabel label="LinkedIn" required={isReq('linkedinUrl')} />
										<TextInput
											value={fields.linkedinUrl}
											onChange={set('linkedinUrl')}
											placeholder="linkedin.com/..."
										/>
									</div>
								)}
								{visible.has('instagramUrl') && (
									<div>
										<FieldLabel label="Instagram" required={isReq('instagramUrl')} />
										<TextInput
											value={fields.instagramUrl}
											onChange={set('instagramUrl')}
											placeholder="@handle"
										/>
									</div>
								)}
								{visible.has('facebookUrl') && (
									<div>
										<FieldLabel label="Facebook" required={isReq('facebookUrl')} />
										<TextInput
											value={fields.facebookUrl}
											onChange={set('facebookUrl')}
											placeholder="facebook.com/..."
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Custom fields (non-default only — no entity/onChangeEntity = non-unified mode) */}
				{hasCustomFields && (
					<div className="mb-5">
						<CustomFieldsSection
							entityType="company"
							values={customFields}
							inline
							showLabel
							onChange={(fieldId, value) => setCustomFields((prev) => ({ ...prev, [fieldId]: value }))}
						/>
					</div>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					type="submit"
					variant="solid"
					size="sm"
					loading={submitting}
					disabled={!fields.companyName.trim() || hasRequiredFieldsMissing}
				>
					Unternehmen erstellen
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

NewCompanyModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onCreated: PropTypes.func.isRequired,
};

export default NewCompanyModal;
